import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

type InteractionType = "call" | "whatsapp" | "email" | "map" | "view";

interface InteractionStats {
  interaction_type: string;
  total_count: number;
  this_month_count: number;
  last_30_days_count: number;
}

export function useVLEInteractions(tenantId?: string) {
  const { tenant } = useAuth();
  const targetTenantId = tenantId || tenant?.id;

  // Log an interaction (public - no auth required)
  const logInteraction = useMutation({
    mutationFn: async ({ 
      tenantId: targetId, 
      type 
    }: { 
      tenantId: string; 
      type: InteractionType;
    }) => {
      const { error } = await supabase
        .from("vle_interactions")
        .insert({
          tenant_id: targetId,
          interaction_type: type,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        } as any);
      
      if (error) {
        console.error("Failed to log interaction:", error);
        // Don't throw - we don't want to break the user experience
      }
    },
  });

  // Increment profile view (simple function call)
  const incrementProfileView = useMutation({
    mutationFn: async (profileTenantId: string) => {
      const { error } = await supabase.rpc("increment_profile_view", {
        p_tenant_id: profileTenantId,
      });
      
      if (error) {
        console.error("Failed to increment profile view:", error);
      }
    },
  });

  // Get interaction stats (for VLE dashboard)
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["vle-interaction-stats", targetTenantId],
    queryFn: async () => {
      if (!targetTenantId) return null;
      
      const { data, error } = await supabase.rpc("get_vle_interaction_stats", {
        p_tenant_id: targetTenantId,
      });
      
      if (error) throw error;
      return data as InteractionStats[];
    },
    enabled: !!targetTenantId,
  });

  // Get profile views count
  const { data: profileViews } = useQuery({
    queryKey: ["profile-views", targetTenantId],
    queryFn: async () => {
      if (!targetTenantId) return 0;
      
      const { data, error } = await supabase
        .from("tenants")
        .select("profile_views")
        .eq("id", targetTenantId)
        .single();
      
      if (error) throw error;
      return (data as any)?.profile_views || 0;
    },
    enabled: !!targetTenantId,
  });

  // Helper to get stat by type
  const getStatByType = (type: InteractionType) => {
    return stats?.find(s => s.interaction_type === type);
  };

  return {
    logInteraction,
    incrementProfileView,
    stats,
    statsLoading,
    profileViews,
    getStatByType,
    totalInteractions: stats?.reduce((sum, s) => sum + Number(s.total_count), 0) || 0,
    monthlyInteractions: stats?.reduce((sum, s) => sum + Number(s.this_month_count), 0) || 0,
  };
}
